import React, { useState, useMemo } from 'react';
import classnames from 'classnames';

import { Paper, Divider, Toolbar, Checkbox, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { Icon, MenuItem, Translate } from '@hydra/common/components';
import TaskItem, { TaskInterface } from '@hydra/routes/marketing-checklist/components/TaskItem';
import TaskFilter from '@hydra/routes/marketing-checklist/components/TaskFilter';

import { getTasksData, updateTasksData } from '@hydra/utils/tasksApi';
import { sendEvent } from '@hydra/utils/gtm';

import { BooleanKeysType, ExecutorMapI18nType, IndexedKeysType } from '@hydra/types/marketing-checklist';

const useStyles = makeStyles(theme => ({
  paper: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius * 2.5
  },
  grid: {
    padding: theme.spacing(6, 2)
  },
  toolbar: {
    padding: theme.spacing(0, 0.25),
    marginBottom: theme.spacing(1.5)
  },
  selected: {
    display: 'flex',
    marginRight: theme.spacing(1)
  },
  icon: {
    marginTop: theme.spacing(0.25),
    marginRight: theme.spacing(0.5)
  },
  filterIcon: {
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing(2)
  },
  item: {
    marginLeft: theme.spacing(0.5)
  },
  capitalize: {
    textTransform: 'capitalize'
  },
  filter: {
    marginRight: theme.spacing(1),
    [theme.breakpoints.down('xs')]: {
      margin: theme.spacing(0.5)
    }
  },
  busy: {
    opacity: 0.5,
    pointerEvents: 'none'
  },
  filterContainer: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap'
  },
  emptyFilter: {
    padding: theme.spacing(2),
    textAlign: 'center'
  }
}));

export function TasksEmpty () {
  const classes = useStyles();
  return (
    <Paper elevation={0} className={classes.paper}>
      <Grid justify='center' container item className={classes.grid}>
        <Grid item xs={1}>
          <Icon type='sign-success' size='md' />
        </Grid>
      </Grid>
    </Paper>
  );
}

const executorMapI18n: ExecutorMapI18nType = {
  designer: 'todo_executor_des',
  marketer: 'todo_executor_mar',
  developer: 'todo_executor_dev'
};

const solvabilityMapI18n: IndexedKeysType = {
  1: 'filter_easyEffort',
  2: 'filter_hardEffort',
  3: 'filter_veryHardEffort'
};

const importanceMapI18n: IndexedKeysType = {
  1: 'filter_lowImpact',
  2: 'filter_mediumImpact',
  3: 'filter_highImpact'
};

const statusMapI18n: BooleanKeysType = {
  true: 'filter_task_done',
  false: 'filter_task_undone'
};

const solvabilityStatus: IndexedKeysType = {
  1: 'easy',
  2: 'hard',
  3: 'veryhard'
};

const importanceStatus: IndexedKeysType = {
  1: 'low',
  2: 'medium',
  3: 'high'
};

export function useTaskPropertyValues (tasks: TaskInterface[], prop: keyof TaskInterface): Set<string> {
  return useMemo(() => (!tasks) ? new Set() : new Set(tasks.map(task => task[prop].toString())), [tasks, prop]);
}

async function doFetch (url: string) {
  const res = await getTasksData({ url });
  return res.tasks;
}

async function doUpdate (params: { url: string, id: string, checked: boolean }) {
  return updateTasksData(params);
}

interface DropdownRenderValue {
  selected: any,
  label: string,
  icon?: string
}

function DropdownRenderValue ({ selected, label, icon }: DropdownRenderValue) {
  const classes = useStyles();
  return (
    <div className={classes.selected}>
      {icon && <Icon type={icon} size='sm' className={classes.icon} />}
      {label && <Translate content={label} className={classes.capitalize} />}
      {selected.length > 0 && <span className={classes.item}>({selected.length})</span>}
    </div>
  );
}

interface TasksTable {
  tasks: TaskInterface[],
  url: string
}

export default function TasksTable ({ tasks: initialTasks, url }: TasksTable) {
  const classes = useStyles();
  const [tasks, setTasks] = useState<TaskInterface[]>(initialTasks);
  const [busy, setBusy] = useState(false);

  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [categoryFilter, setCategoryFilter] = useState<string[]>([]);
  const [executorFilter, setExecutorFilter] = useState<string[]>([]);
  const [importanceFilter, setImportanceFilter] = useState<string[]>([]);
  const [solvabilityFilter, setSolvabilityFilter] = useState<string[]>([]);

  const categoriesAvailable: Set<string> = useTaskPropertyValues(tasks, 'category');
  const executorsAvailable: Set<string> = useTaskPropertyValues(tasks, 'executor');
  const solvabilityAvailable: Set<string> = useTaskPropertyValues(tasks, 'solvability');
  const importanceAvailable: Set<string> = useTaskPropertyValues(tasks, 'importance');
  const statusAvailable: Set<string> = useTaskPropertyValues(tasks, 'checked');

  const filtered = useMemo(() => {
    if (!tasks) return [];
    return tasks.filter(
      task =>
        (categoryFilter.length <= 0 || categoryFilter.includes(task.category)) &&
        (executorFilter.length <= 0 || executorFilter.includes(task.executor)) &&
        (importanceFilter.length <= 0 || importanceFilter.includes(task.importance.toString())) &&
        (solvabilityFilter.length <= 0 || solvabilityFilter.includes(task.solvability.toString())) &&
        (statusFilter.length <= 0 || statusFilter.includes(task.checked.toString()))
    );
  }, [tasks, categoryFilter, executorFilter, importanceFilter, solvabilityFilter, statusFilter]);

  if (!tasks || tasks.length <= 0) {
    return <TasksEmpty />;
  }

  const isAllCategoriesSelected = categoryFilter.length === Array.from(categoriesAvailable).length;
  const isNoneCategoriesSelected = categoryFilter.length === 0;

  const handleOnChangeCategory = (e: React.ChangeEvent<any>) => {
    let active = e.target.value.length > categoryFilter.length;
    const category = active
      ? e.target.value.find((el: string) => categoryFilter.indexOf(el) < 0)
      : categoryFilter.find(el => e.target.value.indexOf(el) < 0);
    if (e.target.value.includes('all')) {
      active = !isAllCategoriesSelected;
      if (!isAllCategoriesSelected) {
        setCategoryFilter(Array.from(categoriesAvailable));
      } else {
        setCategoryFilter([]);
      }
    } else {
      setCategoryFilter(e.target.value);
    }
    sendEvent(`marketingchecklist-filter-category-${category.replace('_', '')}-checkbox`, { active });
  };

  const handleOnCheckTask = async (task: TaskInterface) => {
    try {
      setBusy(true);
      const { id, checked } = task;
      const res = await doUpdate({ url, id, checked: !checked });
      if (res) {
        const data = await doFetch(url);
        setTasks(data);
      }
      setBusy(false);
    } catch (err) {
      setBusy(false);
      throw err;
    }
  };

  return (
    <>
      <Toolbar
        className='nr-task-filterbar'
        classes={{ root: classes.toolbar }}
      >
        <div className={classes.filterIcon}>
          <Icon type='filter' size='sm' />
        </div>
        <div className={classes.filterContainer}>
          <div className={classes.filter}>
            <TaskFilter
              id='filter-category'
              value={categoryFilter}
              onChange={e => handleOnChangeCategory(e)}
              onOpen={() => sendEvent('marketingchecklist-filter-category-dropdown')}
              renderValue={selected => <DropdownRenderValue selected={selected} label='categories' />}
            >
              <MenuItem value='all' key='all'>
                <Checkbox
                  indeterminate={!isAllCategoriesSelected && !isNoneCategoriesSelected}
                  checked={isAllCategoriesSelected}
                />
                <Translate content='crawler_select_all' />
              </MenuItem>
              <Divider />
              {Array.from(categoriesAvailable).map((category, index) => (
                <MenuItem value={category} key={index}>
                  <Checkbox checked={categoryFilter.indexOf(category) > -1} />
                  <Translate content={category} />
                </MenuItem>
              ))}
            </TaskFilter>
          </div>
          <div className={classes.filter}>
            <TaskFilter
              id='filter-executor'
              value={executorFilter}
              onOpen={() => sendEvent('marketingchecklist-filter-role-dropdown')}
              onChange={(e: React.ChangeEvent<any>) => {
                const active = e.target.value.length > executorFilter.length;
                const role = active
                  ? e.target.value.find((el: string) => executorFilter.indexOf(el) < 0)
                  : executorFilter.find(el => e.target.value.indexOf(el) < 0);
                sendEvent(`marketingchecklist-filter-role-${role}-checkbox`, { active });
                setExecutorFilter(e.target.value);
              }}
              renderValue={selected => <DropdownRenderValue selected={selected} label='todo_role' />}
            >
              {Array.from(executorsAvailable).map((executor, index) => (
                <MenuItem value={executor} key={index}>
                  <Checkbox checked={executorFilter.indexOf(executor) > -1} />
                  <Translate content={executorMapI18n[executor as keyof ExecutorMapI18nType]} />
                </MenuItem>
              ))}
            </TaskFilter>
          </div>
          <div className={classes.filter}>
            <TaskFilter
              id='filter-importance'
              value={importanceFilter}
              renderValue={selected => <DropdownRenderValue selected={selected} icon='impact' label='filter_impact' />}
              onChange={(e: React.ChangeEvent<any>) => {
                const active = e.target.value.length > importanceFilter.length;
                const itemClicked = active
                  ? e.target.value.find((el: string) => importanceFilter.indexOf(el) < 0)
                  : importanceFilter.find(el => e.target.value.indexOf(el) < 0);
                const impact = importanceStatus[itemClicked as keyof IndexedKeysType];
                sendEvent(`marketingchecklist-filter-impact-${impact}-checkbox`, { active });
                setImportanceFilter(e.target.value);
              }}
              onOpen={() => sendEvent('marketingchecklist-filter-impact-dropdown')}
            >
              {Array.from(importanceAvailable).map((importance, index) => (
                <MenuItem value={importance} key={index}>
                  <Checkbox checked={importanceFilter.indexOf(importance) > -1} />
                  <Translate content={importanceMapI18n[importance as keyof IndexedKeysType]} />
                </MenuItem>
              ))}
            </TaskFilter>
          </div>
          <div className={classes.filter}>
            <TaskFilter
              id='filter-solvability'
              value={solvabilityFilter}
              renderValue={selected => <DropdownRenderValue selected={selected} icon='gear' label='filter_effort' />}
              onChange={(e: React.ChangeEvent<any>) => {
                const active = e.target.value.length > solvabilityFilter.length;
                const itemClicked = active
                  ? e.target.value.find((el: string) => solvabilityFilter.indexOf(el) < 0)
                  : solvabilityFilter.find(el => e.target.value.indexOf(el) < 0);
                const effort = solvabilityStatus[itemClicked as keyof IndexedKeysType];
                sendEvent(`marketingchecklist-filter-effort-${effort}-checkbox`, { active });
                setSolvabilityFilter(e.target.value);
              }}
              onOpen={() => sendEvent('marketingchecklist-filter-effort-dropdown')}
            >
              {Array.from(solvabilityAvailable).map((solvability, index) => (
                <MenuItem value={solvability} key={index}>
                  <Checkbox checked={solvabilityFilter.indexOf(solvability) > -1} />
                  <Translate content={solvabilityMapI18n[solvability as keyof IndexedKeysType]} />
                </MenuItem>
              ))}
            </TaskFilter>
          </div>
          <div className={classes.filter}>
            <TaskFilter
              id='filter-status'
              value={statusFilter}
              renderValue={selected => <DropdownRenderValue selected={selected} icon='box-check' label='filter_status' />}
              onChange={(e: React.ChangeEvent<any>) => {
                const active = e.target.value.length > statusFilter.length;
                const itemClicked = active
                  ? e.target.value.find((el: string) => statusFilter.indexOf(el) < 0)
                  : statusFilter.find(el => e.target.value.indexOf(el) < 0);
                const status = itemClicked ? 'checked' : 'unchecked';
                sendEvent(`marketingchecklist-filter-status-${status}-checkbox`, { active });
                setStatusFilter(e.target.value);
              }}
              onOpen={() => sendEvent('marketingchecklist-filter-status-dropdown')}
            >
              {Array.from(statusAvailable).map((status, index) => (
                <MenuItem value={status} key={index}>
                  <Checkbox checked={statusFilter.indexOf(status) > -1} />
                  <Translate content={statusMapI18n[status as keyof BooleanKeysType]} />
                </MenuItem>
              ))}
            </TaskFilter>
          </div>
        </div>
      </Toolbar>
      <Grid item className={classnames({ [classes.busy]: busy })}>
        {
          filtered.length === 0
            ? (
              <Paper
                elevation={0}
                className={classnames(
                  classes.emptyFilter,
                  classes.paper
                )}
              >
                <Translate content='tips_filter_empty_state' />
              </Paper>
              )
            : filtered.map(task => <TaskItem onChange={() => handleOnCheckTask(task)} key={task.id} task={task} />)
        }
      </Grid>
    </>
  );
}
